import moment from "moment";
import React, { forwardRef } from "react";

const formatMoney = (amount) =>
  `₹ ${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const joinParts = (parts) => parts.filter(Boolean).join(", ");

// The fee document can carry a breakdown or just a total, depending on how the
// appointment was created (patient app vs. clinic desk). Print whichever of
// these the backend actually sent and skip the rest, so no row reads "undefined".
const FEE_LINES = [
  { key: "consultation", label: "Consultation fee" },
  { key: "platform", label: "Platform fee" },
  { key: "convenience", label: "Convenience fee" },
  { key: "tax", label: "Tax" },
  { key: "discount", label: "Discount", negative: true },
];

// Last 6 characters of the appointment id: stable, unique per booking, and
// short enough to quote over the phone. There is no invoice number from the API.
export const invoiceNumber = (appointment) => {
  const id = String(appointment?._id || "");
  return id ? `INV-${id.slice(-6).toUpperCase()}` : "INV-—";
};

const PAYMENT_LABELS = {
  ONLINE: "Paid online",
  OFFLINE: "Paid at clinic",
  CASH: "Cash",
};

const InvoiceDocument = forwardRef(({ appointment }, ref) => {
  const isFree = Boolean(appointment?.isFree);
  const fees = appointment?.fees || {};
  const total = isFree ? 0 : (fees?.total ?? 0);

  const breakdown = isFree
    ? []
    : FEE_LINES.map((line) => ({ ...line, amount: fees?.[line.key] })).filter(
        (line) => Number.isFinite(Number(line.amount)) && line.amount !== null,
      );

  const patient = appointment?.patient || appointment?.unregisteredPatient;
  const patientAddress = joinParts([
    appointment?.address?.address1,
    appointment?.address?.address2,
    appointment?.address?.cityVillage,
    appointment?.address?.district,
    appointment?.address?.state,
    appointment?.address?.pincode,
  ]);

  const clinicAddress = joinParts([
    appointment?.clinic?.address?.street,
    appointment?.clinic?.address?.area,
    appointment?.clinic?.address?.city,
    appointment?.clinic?.address?.state,
    appointment?.clinic?.address?.pincode,
  ]);

  const isOnline = String(appointment?.type || "")
    .toUpperCase()
    .includes("ONLINE");
  const visitLabel = isOnline ? "Online consultation" : "In-clinic consultation";

  const slot =
    appointment?.slot?.startTime && appointment?.slot?.endTime
      ? `${moment(appointment.slot.startTime, "HH:mm").format(
          "hh:mm A",
        )} - ${moment(appointment.slot.endTime, "HH:mm").format("hh:mm A")}`
      : null;

  const isCancelled = String(appointment?.status || "").toUpperCase() ===
    "CANCELLED";
  const isRefunded = String(appointment?.status || "").toUpperCase() ===
    "REFUNDED";

  return (
    <div className="invoice_doc" ref={ref}>
      <div className="invoice_doc_head">
        <div>
          <h2>{appointment?.clinic?.name || "Clinic"}</h2>
          {appointment?.doctor?.name && <p>Dr. {appointment.doctor.name}</p>}
          {clinicAddress && <p>{clinicAddress}</p>}
          {appointment?.doctor?.phone && <p>Contact: {appointment.doctor.phone}</p>}
        </div>
        <div className="invoice_doc_meta">
          <span className="invoice_doc_title">Invoice</span>
          <p>{invoiceNumber(appointment)}</p>
          {appointment?.createdAt && (
            <p>
              Issued:{" "}
              {moment(appointment.createdAt).format("DD MMM YYYY, hh:mm A")}
            </p>
          )}
        </div>
      </div>

      {(isCancelled || isRefunded) && (
        <div className="invoice_doc_banner">
          {isRefunded
            ? "This appointment was refunded."
            : "This appointment was cancelled."}
        </div>
      )}

      <div className="invoice_doc_parties">
        <div>
          <h3>Billed to</h3>
          <p className="invoice_doc_strong">{patient?.name || "—"}</p>
          {patient?.phone && <p>{patient.phone}</p>}
          {patientAddress && <p>{patientAddress}</p>}
          {appointment?.appointmentPatientId && (
            <p>Patient ID: {appointment.appointmentPatientId}</p>
          )}
        </div>
        <div>
          <h3>Appointment</h3>
          <p className="invoice_doc_strong">{visitLabel}</p>
          {appointment?.date && (
            <p>{moment(appointment.date).format("DD MMM YYYY")}</p>
          )}
          {slot && <p>{slot}</p>}
        </div>
      </div>

      <table className="invoice_doc_table">
        <thead>
          <tr>
            <th>Description</th>
            <th className="invoice_doc_amount_col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.length ? (
            breakdown.map((line) => (
              <tr key={line.key}>
                <td>{line.label}</td>
                <td className="invoice_doc_amount_col">
                  {line.negative ? "- " : ""}
                  {formatMoney(line.amount)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td>
                {visitLabel}
                {appointment?.doctor?.name ? ` — Dr. ${appointment.doctor.name}` : ""}
              </td>
              <td className="invoice_doc_amount_col">
                {isFree ? "FREE" : formatMoney(total)}
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td className="invoice_doc_amount_col">
              {isFree ? "FREE" : formatMoney(total)}
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="invoice_doc_payment">
        Payment:{" "}
        {isFree
          ? "No charge"
          : PAYMENT_LABELS[String(appointment?.paymentType || "").toUpperCase()] ||
            "Paid at clinic"}
      </p>

      <p className="invoice_doc_note">
        Computer generated invoice. No signature required.
      </p>
    </div>
  );
});

export default InvoiceDocument;
